import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChitContext = createContext(null);

// Hook simplu prin care orice componentă accesează datele și acțiunile.
export function useChit() {
  return useContext(ChitContext);
}

export function ChitProvider({ children }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // La pornire, aducem grupurile contului din backend.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/groups`, { withCredentials: true });
        setGroups(r.data);
      } catch {
        setGroups([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Actualizează un grup DOAR în state (UI optimist). Salvarea pe server
  // se face separat, prin endpoint-uri granulare atomice.
  const localUpdate = (groupId, updater) => {
    setGroups((prev) => prev.map((g) => (g.id === groupId ? updater(g) : g)));
  };

  const refetchGroups = async () => {
    try {
      const r = await axios.get(`${API}/groups`, { withCredentials: true });
      setGroups(r.data);
    } catch {
      // ignorăm
    }
  };

  // --- Grupuri ---

  const createGroup = async (name, memberNames) => {
    const members = memberNames
      .filter((n) => n.trim() !== "")
      .map((n) => ({ id: crypto.randomUUID(), name: n.trim() }));
    const r = await axios.post(
      `${API}/groups`,
      { name: name.trim(), members },
      { withCredentials: true }
    );
    setGroups((prev) => [r.data, ...prev]);
    return r.data.id;
  };

  const deleteGroup = async (groupId) => {
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
    try {
      await axios.delete(`${API}/groups/${groupId}`, { withCredentials: true });
    } catch {
      toast.error("Eroare la ștergere");
    }
  };

  const getGroup = (groupId) => groups.find((g) => g.id === groupId);

  // --- Membri ---

  const addMember = async (groupId, name) => {
    const member = { id: crypto.randomUUID(), name: name.trim() };
    localUpdate(groupId, (g) => ({ ...g, members: [...g.members, member] }));
    try {
      await axios.post(`${API}/groups/${groupId}/members`, member, {
        withCredentials: true,
      });
    } catch (e) {
      refetchGroups();
      throw e;
    }
  };

  // --- Cheltuieli ---

  const addExpense = async (groupId, expense) => {
    const newExpense = {
      id: crypto.randomUUID(),
      description: expense.description.trim(),
      amount: expense.amount,
      paidBy: expense.paidBy,
      category: expense.category || "altele",
      splitMode: expense.splitMode || "egal",
      splits: expense.splits,
      currency: expense.currency || "RON",
      originalAmount: expense.originalAmount ?? null,
      receiptId: expense.receiptId ?? null,
      receiptName: expense.receiptName ?? null,
      createdAt: expense.createdAt || new Date().toISOString(),
    };
    localUpdate(groupId, (g) => ({ ...g, expenses: [newExpense, ...g.expenses] }));
    try {
      await axios.post(`${API}/groups/${groupId}/expenses`, newExpense, {
        withCredentials: true,
      });
    } catch (e) {
      refetchGroups();
      throw e;
    }
  };

  const updateExpense = async (groupId, expenseId, updated) => {
    const fields = {
      description: updated.description.trim(),
      amount: updated.amount,
      paidBy: updated.paidBy,
      category: updated.category || "altele",
      splitMode: updated.splitMode || "egal",
      splits: updated.splits,
      currency: updated.currency || "RON",
      originalAmount: updated.originalAmount ?? null,
      receiptId: updated.receiptId ?? null,
      receiptName: updated.receiptName ?? null,
      createdAt: updated.createdAt || new Date().toISOString(),
    };
    localUpdate(groupId, (g) => ({
      ...g,
      expenses: g.expenses.map((e) =>
        e.id === expenseId ? { ...e, ...fields } : e
      ),
    }));
    try {
      await axios.put(`${API}/groups/${groupId}/expenses/${expenseId}`, fields, {
        withCredentials: true,
      });
    } catch (e) {
      refetchGroups();
      throw e;
    }
  };

  const deleteExpense = async (groupId, expenseId) => {
    localUpdate(groupId, (g) => ({
      ...g,
      expenses: g.expenses.filter((e) => e.id !== expenseId),
    }));
    try {
      await axios.delete(`${API}/groups/${groupId}/expenses/${expenseId}`, {
        withCredentials: true,
      });
    } catch {
      toast.error("Eroare la ștergere");
      refetchGroups();
    }
  };

  // --- Plăți de decontare (marchează o datorie ca „plătită") ---

  const addPayment = async (groupId, payment) => {
    const newPayment = {
      id: crypto.randomUUID(),
      from: payment.from,
      to: payment.to,
      amount: payment.amount,
      createdAt: new Date().toISOString(),
    };
    localUpdate(groupId, (g) => ({
      ...g,
      payments: [newPayment, ...(g.payments || [])],
    }));
    try {
      await axios.post(`${API}/groups/${groupId}/payments`, newPayment, {
        withCredentials: true,
      });
    } catch {
      toast.error("Eroare la salvare");
      refetchGroups();
    }
  };

  const deletePayment = async (groupId, paymentId) => {
    localUpdate(groupId, (g) => ({
      ...g,
      payments: (g.payments || []).filter((p) => p.id !== paymentId),
    }));
    try {
      await axios.delete(`${API}/groups/${groupId}/payments/${paymentId}`, {
        withCredentials: true,
      });
    } catch {
      toast.error("Eroare la ștergere");
      refetchGroups();
    }
  };

  // mode: "claim" (revendică un membru existent) sau "new" (membru nou)
  const joinGroup = async (groupId, mode, memberId = null) => {
    const r = await axios.post(
      `${API}/groups/${groupId}/join`,
      { mode, memberId },
      { withCredentials: true }
    );
    await refetchGroups();
    return r.data;
  };

  const renameGroup = async (groupId, name) => {
    localUpdate(groupId, (g) => ({ ...g, name: name.trim() }));
    try {
      await axios.put(
        `${API}/groups/${groupId}/name`,
        { name: name.trim() },
        { withCredentials: true }
      );
    } catch (e) {
      toast.error("Eroare la redenumire");
      refetchGroups();
      throw e;
    }
  };

  const leaveGroup = async (groupId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/leave`, {}, { withCredentials: true });
      setGroups((prev) => prev.filter((g) => g.id !== groupId));
    } catch (e) {
      toast.error("Eroare la părăsirea grupului");
      throw e;
    }
  };

  const value = {
    groups,
    loading,
    createGroup,
    deleteGroup,
    getGroup,
    addMember,
    addExpense,
    updateExpense,
    deleteExpense,
    addPayment,
    deletePayment,
    refetchGroups,
    joinGroup,
    renameGroup,
    leaveGroup,
  };

  return <ChitContext.Provider value={value}>{children}</ChitContext.Provider>;
}
